import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './AudioPlayerList.module.scss';
import classNames from 'classnames';

// Components
import AudioPlayer from '../AudioPlayer';
import Button from '../Button';
import RawHtml from '../RawHtml';
import SingleChooser from '../SingleChooser';
import {
    setGetArguments,
    useProductSelection,
} from '../../utils/singleProductChooser';
import { useRouter } from 'next/router';
import { useSampleDownloadSelector } from '../../utils/useSampleDownloadSelector';

const AudioPlayerList = ({
    title = '',
    preamble,
    link = {
        text: '',
        href: '',
    },
    column = true,
    download,
    hideChooser = false,
    items = [],
    productList,
    selectedProductId,
    separator = false,
    onDownload
}) => {
    const [mounted, setMounted] = useState(false);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

    // Product selector
    const router = useRouter();
    const { selectedProduct, setProductById } = useProductSelection({
        ...productList,
        selectedProductId,
    });
    useEffect(() => {
        router.replace(
            setGetArguments(
                window.location,
                selectedProduct ? selectedProduct.value : 0
            ),
            undefined,
            { scroll: false }
        );
    }, [selectedProduct]);

    // Download selected
    const { getDownloadSelectedUrl, toggleSelectedItem, selectedItems } =
        useSampleDownloadSelector();
    const downloadSelectedUrl = getDownloadSelectedUrl(selectedProductId);
    const handleToggleItem = (e) => {
        toggleSelectedItem(e.target.value, e.target.checked);
    };

    const stopCurrentlyPlaying = () => {
        if (currentlyPlaying) {
            currentlyPlaying.pause();
        }
    };

    useEffect(() => {
        setMounted(true);
    }, []);

    return (
        <div
            className={classNames(styles['AudioPlayerList'], {
                [styles['AudioPlayerList--Separator']]: separator,
            })}>
            <div
                className={classNames(styles['AudioPlayerList__Container'], {
                    [styles['AudioPlayerList__Container--Column']]: column,
                })}>
                <div className={styles['AudioPlayerList__TextContainer']}>
                    {title && (
                        <h2 className={styles['AudioPlayerList__Title']}>
                            {title}
                        </h2>
                    )}
                    {preamble && (
                        <div className={styles['AudioPlayerList__Preamble']}>
                            <RawHtml html={preamble} />
                        </div>
                    )}
                    {link && link.href && (
                        <div className={styles['AudioPlayerList__Button']}>
                            <Button link={link} />
                        </div>
                    )}
                </div>

                {productList && !hideChooser && (
                    <div className={styles['AudioPlayerList__Filters']}>
                        <SingleChooser
                            modifiers={['alternative']}
                            placeholder={
                                selectedProduct
                                    ? selectedProduct.label
                                    : 'Product'
                            }
                            activeValue={
                                selectedProduct ? selectedProduct.value : null
                            }
                            onChange={setProductById}
                            tooltip={
                                'Select your product to view compatible content'
                            }
                            options={productList.activeProducts}
                            secondaryOptionName={'Legacy products'}
                            secondaryOptions={productList.legacyProducts}
                        />
                    </div>
                )}

                {mounted && (
                    <div className={styles['AudioPlayerList__AudioContainer']}>
                        {items.map((item, index) => {
                            return (
                                <div
                                    key={index}
                                    className={
                                        styles[
                                            'AudioPlayerList__PlayerContainer'
                                        ]
                                    }>
                                    <AudioPlayer
                                        audio={{
                                            title: item.title,
                                            file: item.previewFile
                                                ? item.previewFile
                                                : item.file,
                                        }}
                                        onToggleDownload={handleToggleItem}
                                        id={item.id}
                                        download={download}
                                        productList={
                                            item.compatibleProducts
                                                ? item.compatibleProducts
                                                : productList
                                        }
                                        selectedProduct={selectedProduct}
                                        onPlay={(player) => {
                                            if (currentlyPlaying !== player) {
                                                stopCurrentlyPlaying();
                                                setCurrentlyPlaying(player);
                                            }
                                        }}
                                        onPause={() => {
                                            stopCurrentlyPlaying();
                                            setCurrentlyPlaying(null);
                                        }}
                                        onDownload={onDownload}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

AudioPlayerList.propTypes = {
    title: PropTypes.string.isRequired,
    preamble: PropTypes.string,
    link: PropTypes.shape({
        text: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
    }),
    items: PropTypes.arrayOf(
        PropTypes.shape({
            audio: PropTypes.shape({
                file: PropTypes.string.isRequired,
                title: PropTypes.string,
            }),
        })
    ).isRequired,
    handleSelectedProductOnChange: PropTypes.func,
};

export default AudioPlayerList;
